import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, Building2, Calendar, FileText, Users, 
  Settings, Plus, TrendingUp, TrendingDown, DollarSign,
  Clock, CheckCircle, Target, Award, Star, Heart, ThumbsUp, 
  MessageSquare, Phone, Mail, Map, Navigation, Compass, Activity, 
  Globe, MapPin, Package, Layers, GitBranch, GitCommit, GitPullRequest,
  GitMerge, Workflow, UserCheck, ArrowRight, ArrowLeft, Play, Pause,
  Stop, RotateCcw, List, Grid, PieChart, Monitor, Smartphone,
  Wifi, Lock, Eye, EyeOff, Save, Upload, Trash2, Edit, Check, Clock4,
  CalendarDays, Brain, Palette, Key, Database, Zap, Percent, Bell, 
  Search, Filter, Download, RefreshCw, Sun, Moon, ChevronRight, 
  ChevronLeft, MoreHorizontal, Sparkles
} from 'lucide-react';

const ProjexLayout = () => {
  const [hoveredTab, setHoveredTab] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState(5);
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    { 
      id: 'dashboard',
      path: '/projex', 
      label: 'Dashboard', 
      icon: BarChart3,
      description: 'Project overview and KPIs',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500'
    },
    { 
      id: 'projects',
      path: '/projex/projects', 
      label: 'Projects', 
      icon: Building2,
      description: 'Project management and tracking',
      color: 'from-purple-500 to-indigo-500',
      bgColor: 'bg-purple-500'
    },
    { 
      id: 'timeline',
      path: '/projex/timeline', 
      label: 'Timeline', 
      icon: Calendar,
      description: 'Gantt charts and milestones',
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-500'
    },
    { 
      id: 'documents',
      path: '/projex/documents', 
      label: 'Documents', 
      icon: FileText,
      description: 'File management and version control',
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-500'
    },
    { 
      id: 'team',
      path: '/projex/team', 
      label: 'Team', 
      icon: Users,
      description: 'Resource allocation and collaboration',
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-500'
    },
    { 
      id: 'analytics',
      path: '/projex/analytics', 
      label: 'Analytics', 
      icon: Activity,
      description: 'Performance metrics and reporting',
      color: 'from-indigo-500 to-purple-500',
      bgColor: 'bg-indigo-500'
    },
    { 
      id: 'settings',
      path: '/projex/settings', 
      label: 'Settings', 
      icon: Settings,
      description: 'System configuration and preferences',
      color: 'from-gray-500 to-slate-500',
      bgColor: 'bg-gray-500'
    }
  ];

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <div className={`min-h-screen relative transition-all duration-500 ${
      isDarkMode 
        ? 'bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900' 
        : 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50'
    }`}>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-20 left-10 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob ${
          isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-300/30'
        }`}></div>
        <div className={`absolute top-40 right-10 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000 ${
          isDarkMode ? 'bg-purple-500/20' : 'bg-purple-300/30'
        }`}></div>
        <div className={`absolute -bottom-8 left-20 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000 ${
          isDarkMode ? 'bg-blue-500/20' : 'bg-blue-300/30'
        }`}></div>
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent)]' 
            : 'bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.8),transparent)]'
        }`}></div>
      </div>

      {/* Full-Screen Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative sticky top-0 z-30 border-b transition-all duration-500 ${
          isDarkMode 
            ? 'bg-black/20 backdrop-blur-xl border-white/10' 
            : 'bg-white/80 backdrop-blur-xl border-gray-200/50'
        }`}
      >
        <div className="w-full px-8 py-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-6">
              <motion.div 
                className="p-4 bg-gradient-to-br from-indigo-500 via-purple-500 to-blue-500 rounded-2xl shadow-2xl"
                whileHover={{ scale: 1.05, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Building2 className="h-8 w-8 text-white" />
              </motion.div>
              <div>
                <h1 className={`text-3xl font-bold bg-gradient-to-r bg-clip-text text-transparent ${
                  isDarkMode 
                    ? 'from-white to-indigo-200' 
                    : 'from-gray-900 to-indigo-600'
                }`}>
                  Projex Platform
                </h1>
                <p className={`text-sm flex items-center space-x-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  <Sparkles className="h-4 w-4 text-yellow-400" />
                  <span>Next-Generation Project Management System</span>
                </p>
              </div>
            </div>
            
            {/* Header Actions */}
            <div className="flex items-center space-x-4">
              <div className="relative hidden md:block">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`} />
                <input
                  type="text"
                  placeholder="Search projects, documents, team..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-80 pl-10 pr-4 py-2 rounded-xl border transition-all ${
                    isDarkMode 
                      ? 'bg-white/10 border-white/20 text-white placeholder-gray-400 focus:border-indigo-500' 
                      : 'bg-white/80 border-gray-200 text-gray-900 placeholder-gray-500 focus:border-indigo-500'
                  } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
                />
              </div>
              
              <button className={`p-3 rounded-xl hover:transition-all duration-300 relative ${
                isDarkMode 
                  ? 'bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}>
                <Bell className="h-5 w-5" />
                {notifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
                )}
              </button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleDarkMode}
                className={`p-3 rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center space-x-2 ${
                  isDarkMode 
                    ? 'bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
              >
                <Plus className="h-5 w-5" />
                <span>New Project</span>
              </motion.button>
            </div>
          </div>
          
          {/* Centered Navigation Tabs */}
          <div className="flex justify-center">
            <div className="flex space-x-1 max-w-full">
              {navigationItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = isActiveRoute(item.path);
                const isHovered = hoveredTab === index;
                
                return (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onHoverStart={() => setHoveredTab(index)}
                    onHoverEnd={() => setHoveredTab(null)}
                  >
                    <button
                      onClick={() => navigate(item.path)}
                      className={`group relative flex flex-col items-center space-y-2 px-4 py-3 rounded-2xl text-sm font-medium transition-all duration-300 ${
                        isActive
                          ? 'bg-gradient-to-r ' + item.color + ' text-white shadow-2xl scale-105'
                          : isDarkMode
                            ? 'text-gray-300 hover:text-white hover:bg-white/10 hover:shadow-xl'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-white/50 hover:shadow-xl'
                      }`}
                    >
                      {/* Background Glow Effect */}
                      {isActive && (
                        <motion.div
                          className={`absolute inset-0 bg-gradient-to-r ${item.color} rounded-2xl opacity-30 blur-xl`}
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />
                      )}
                      
                      {/* Icon Container */}
                      <div className={`relative p-2 rounded-xl transition-all duration-300 ${
                        isActive 
                          ? 'bg-white/20 backdrop-blur-sm' 
                          : isDarkMode
                            ? 'bg-white/5 group-hover:bg-white/10'
                            : 'bg-gray-100 group-hover:bg-gray-200'
                      }`}>
                        <Icon className={`h-5 w-5 transition-all duration-300 ${
                          isActive ? 'text-white' : isDarkMode ? 'text-gray-300 group-hover:text-white' : 'text-gray-600 group-hover:text-gray-900'
                        }`} />
                        
                        {/* Hover Animation */}
                        {isHovered && !isActive && (
                          <motion.div
                            className={`absolute inset-0 rounded-xl ${
                              isDarkMode 
                                ? 'bg-gradient-to-r from-indigo-500/20 to-purple-500/20' 
                                : 'bg-gradient-to-r from-indigo-100/50 to-purple-100/50'
                            }`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                          />
                        )}
                      </div>
                      
                      {/* Label */}
                      <span className="font-semibold text-xs">{item.label}</span>
                      
                      {/* Description Tooltip */}
                      <AnimatePresence>
                        {isHovered && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className={`absolute -bottom-12 left-1/2 transform -translate-x-1/2 px-3 py-1 backdrop-blur-sm text-xs rounded-lg whitespace-nowrap z-50 border ${
                              isDarkMode 
                                ? 'bg-black/90 text-white border-white/20' 
                                : 'bg-white/90 text-gray-900 border-gray-200'
                            }`}
                          >
                            {item.description}
                            <div className={`absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 w-2 h-2 border-l border-t rotate-45 ${
                              isDarkMode 
                                ? 'bg-black/90 border-white/20' 
                                : 'bg-white/90 border-gray-200'
                            }`}></div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Full-Screen Content Area */}
      <div className="relative w-full min-h-[calc(100vh-200px)]">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ProjexLayout; 